import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BaseScene } from './base-scene';

/**
 * Configuration for GLB animation scene
 */
export interface GLBSceneConfig {
  modelUrl: string;
  scale?: number;
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  autoPlay?: boolean;
  buttonEnabled?: boolean;
}

/**
 * Scene that loads and displays an animated GLB model
 */
export class GLBAnimationScene extends BaseScene {
  private config: GLBSceneConfig;
  private mixer: THREE.AnimationMixer | null = null;
  private action: THREE.AnimationAction | null = null;
  private model: THREE.Object3D | null = null;
  private button: HTMLDivElement | null = null;
  private loader: GLTFLoader | null = null;

  constructor(config: GLBSceneConfig) {
    super({
      id: 'glb-animation',
      name: 'GLB Animation',
      description: 'Animated 3D model loaded from GLB file'
    });
    this.config = config;
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) {
      console.warn('GLBAnimationScene already initialized');
      return;
    }

    // Add root to parent
    parent.add(this.root);

    // Add lighting
    this.addAmbientLight(0xffffff, 1);

    // Create GLTFLoader
    this.loader = new GLTFLoader();

    // Load the model
    this.loadModel();

    // Create button if enabled
    if (this.config.buttonEnabled !== false) {
      this.createButton();
    }

    this.initialized = true;
  }

  private loadModel(): void {
    if (!this.loader) return;

    console.log(`🔄 Loading GLB model: ${this.config.modelUrl}`);

    this.loader.load(
      this.config.modelUrl,
      (gltf) => {
        this.model = gltf.scene;

        // Apply transformations
        if (this.config.scale) {
          this.model.scale.setScalar(this.config.scale);
        }

        if (this.config.position) {
          this.model.position.copy(this.config.position);
        }

        if (this.config.rotation) {
          this.model.rotation.copy(this.config.rotation);
        } else {
          // Default rotation for image tracking (flat on surface)
          this.model.rotateX(Math.PI / 2);
        }

        // Setup animation if available
        if (gltf.animations && gltf.animations.length > 0) {
          this.mixer = new THREE.AnimationMixer(this.model);
          this.action = this.mixer.clipAction(gltf.animations[0]);

          if (this.config.autoPlay) {
            this.action.play();
          }

          console.log(`🎬 Animation loaded: ${gltf.animations.length} clip(s)`);
        }

        // Add to scene
        this.root.add(this.model);

        console.log(`✅ GLB model loaded successfully`);
        console.log(`   Position:`, this.model.position);
        console.log(`   Scale:`, this.model.scale);
        console.log(`   Rotation:`, this.model.rotation);
      },
      undefined,
      (error) => {
        console.error(`❌ Error loading GLB model:`, error);
      }
    );
  }

  private createButton(): void {
    // Create button element
    this.button = document.createElement('div');
    this.button.setAttribute('class', 'circle');
    this.button.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
    `;
    this.button.textContent = '▶';

    // Add click handler
    this.button.onclick = () => {
      if (this.action) {
        this.action.play();
        console.log('🎬 Animation playing');
      }
    };

    // Add to document
    document.body.appendChild(this.button);
  }

  update(delta: number): void {
    if (!this.initialized) return;

    // Update animation mixer
    if (this.mixer) {
      this.mixer.update(delta);
    }
  }

  dispose(): void {
    // Remove button
    if (this.button && this.button.parentElement) {
      this.button.parentElement.removeChild(this.button);
      this.button = null;
    }

    // Dispose mixer
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer = null;
    }

    this.action = null;
    this.model = null;
    this.loader = null;

    super.dispose();
  }

  /**
   * Play the animation
   */
  playAnimation(): void {
    if (this.action) {
      this.action.play();
    }
  }

  /**
   * Stop the animation
   */
  stopAnimation(): void {
    if (this.action) {
      this.action.stop();
    }
  }

  /**
   * Get the loaded model
   */
  getModel(): THREE.Object3D | null {
    return this.model;
  }

  /**
   * Get the animation mixer
   */
  getMixer(): THREE.AnimationMixer | null {
    return this.mixer;
  }
}
